/**
 * Okta OIDC integration via oidc-client-ts.
 *
 * Architecture: pure SPA + PKCE. The Okta app in the admin console is
 * configured as "Single-Page Application" — no client secret. Tokens
 * (id_token + access_token) come back to /auth/callback and are stored in
 * localStorage by the UserManager. API calls attach the access_token as
 * `Authorization: Bearer …`; API Gateway validates the JWT against Okta's
 * JWKS via the HttpJwtAuthorizer defined in infra/lib/api-stack.ts.
 *
 * No Cognito User Pool involved — we removed AuthStack when picking this
 * approach. Okta is the only IdP and the only issuer.
 *
 * The issuer + client ID are per-environment and come from Vite env vars,
 * mirroring the per-env Okta config the infra reads in infra/bin/app.ts:
 *   - .env.development → preview tenant (local `vite dev` + the dev deploy,
 *     which builds with `--mode development`)
 *   - .env.production  → real prod tenant (default `vite build`)
 * Each origin that runs this bundle must be registered in the matching Okta
 * app's Sign-in / Sign-out redirect URIs.
 *
 * The `userManager` singleton, the auth context, and the `useAuth` hook live
 * in `./authContext` (non-component module) so this file can export only the
 * `<AuthProvider>` component — required for React Fast Refresh.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { type User } from "oidc-client-ts";
import { setAuthTokenGetter } from "../api/client";
import { AuthContext, userManager, type AuthContextValue } from "./authContext";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Resolve an existing session on mount and subscribe to UserManager events
  // so React state stays in sync with token expiry / silent renew / signouts.
  useEffect(() => {
    let cancelled = false;

    userManager
      .getUser()
      .then((u) => {
        if (cancelled) return;
        // Treat expired tokens as signed-out — the AuthGuard will redirect.
        if (u && !u.expired) {
          setUser(u);
        } else {
          setUser(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    const onUserLoaded = (u: User) => setUser(u);
    const onUserUnloaded = () => setUser(null);
    const onAccessTokenExpired = () => setUser(null);
    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addUserUnloaded(onUserUnloaded);
    userManager.events.addAccessTokenExpired(onAccessTokenExpired);

    return () => {
      cancelled = true;
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeAccessTokenExpired(onAccessTokenExpired);
    };
  }, []);

  // Tell the API client how to get the current access token. This wires the
  // module-level getter so every fetch() picks up the latest token without
  // having to thread the auth context through every call site.
  //
  // Why a ref + render-body update (not a useEffect): React runs child
  // mount effects BEFORE parent mount effects. If a page's usePoll fires
  // its first tick before AuthProvider's effect re-runs after `user`
  // changes, `getAuthToken()` returns null and the initial request comes
  // back 401. Updating the ref during render and reading it lazily means
  // the getter always sees the latest user without depending on effect
  // ordering.
  const userRef = useRef<User | null>(user);
  userRef.current = user;
  useEffect(() => {
    setAuthTokenGetter(() => userRef.current?.access_token ?? null);
  }, []);

  const signIn = useCallback(async (returnTo?: string) => {
    await userManager.signinRedirect({
      // Stash the in-app path the user was trying to reach so the callback
      // page can route them back after Okta returns.
      state: { returnTo: returnTo ?? window.location.pathname },
    });
  }, []);

  const signOut = useCallback(async () => {
    await userManager.signoutRedirect();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      displayName:
        (user?.profile.name as string | undefined) ??
        (user?.profile.email as string | undefined) ??
        (user?.profile.preferred_username as string | undefined) ??
        user?.profile.sub ??
        null,
      email: (user?.profile.email as string | undefined) ?? null,
      signIn,
      signOut,
    }),
    [user, isLoading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
